import React, { useMemo, useCallback, useEffect, useState } from 'react';
import { Box } from '@tanium/react-box';
import { CloseIcon } from '@tanium/react-graphics';
import { IconButton } from '@tanium/react-icon-button';
import { Modal } from '@tanium/react-modal';
import { Stack } from '@tanium/react-stack';
import { useTranslation } from 'react-i18next';
import { Formik, FormikProps, FormikConfig } from 'formik';
import { ConfigureIdpRequest } from '../../models/IdpModels';
import { IdpSetupConfiguration } from './IdpSetupConfiguration';
import { IdpFormValidationSchema } from './IdpFormValidationSchema';
import { useHasLinkedCloudInstances } from '../../hooks/useHasLinkedCloudInstances';
import { useIdentityProviderConfigs } from '../../hooks/useIdentityProviderConfigs';
import { DEFAULT_EMAIL_ATTRIBUTE_NAME } from '../../utilities/constants';
import { ConsoleIdpInstancePicker } from './ConsoleIdpInstancePicker';
import { IdpScope, IdpScopeCard, IdpScopeGrid, IdpScopeValue } from './IdpScopeCard';

interface IdpSetupModalProps {
  isOpen: boolean;
  onRequestClose: () => void;
  initialConfig?: ConfigureIdpRequest;
  initialScope?: IdpScopeValue;
  showInstanceFriendlyNames?: boolean;
}

export const IdpSetupModal: React.VFC<IdpSetupModalProps> = ({
  initialConfig,
  isOpen,
  onRequestClose,
  initialScope = IdpScope.Account,
  showInstanceFriendlyNames = false,
}) => {
  const { t } = useTranslation();
  const [scope, setScope] = useState<IdpScopeValue>(initialScope);
  useEffect(() => {
    if (isOpen) {
      setScope(initialScope);
    }
  }, [isOpen, initialScope]);
  const hasCloudInstances = useHasLinkedCloudInstances();
  const showScopeSelector = !initialConfig && hasCloudInstances;
  const showConsolePicker = showScopeSelector && scope === IdpScope.Console;
  const { idpConfigs } = useIdentityProviderConfigs();

  const generateUniqueIdpName = useCallback(() => {
    let idpName = crypto.randomUUID().split('-').join('').substring(0, 5);
    // if the idp name already exists, generate a new one
    /* istanbul ignore if */
    if (idpConfigs.find((idp) => idp.name === idpName)) {
      idpName = generateUniqueIdpName();
    }
    return idpName;
  }, [idpConfigs]);

  const modalHeader = (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Box mt={-2} mb={-2}>
        <h4>{initialConfig ? t('Update Identity Provider Settings') : t('Add Identity Provider')}</h4>
      </Box>
      <IconButton icon={CloseIcon} height={16} width={16} label={t('Close')} onClick={onRequestClose} />
    </Stack>
  );

  const initialValues = useMemo(
    () =>
      ({
        id: initialConfig?.id,
        name: initialConfig?.name || generateUniqueIdpName(),
        displayName: initialConfig?.displayName ?? '',
        emailAttributeName: initialConfig?.emailAttributeName ?? DEFAULT_EMAIL_ATTRIBUTE_NAME,
        samlMetadataUrl: initialConfig?.samlMetadataUrl ?? '',
        samlMetadataFile: initialConfig?.samlMetadataFile ?? '',
        enableTaniumConsoleAuth: initialConfig?.enableTaniumConsoleAuth ?? false,
        enableAccountPortalAuth: initialConfig?.enableAccountPortalAuth ?? true,
        enableIdpSignout: initialConfig?.enableIdpSignout ?? false,
        domainNames: initialConfig?.domainNames ?? [],
        roleMappers: initialConfig?.roleMappers,
      } as ConfigureIdpRequest),
    [initialConfig, generateUniqueIdpName]
  );

  const formikProps = {
    initialValues,
    validationSchema: IdpFormValidationSchema(t),
  } as FormikConfig<ConfigureIdpRequest>;

  return (
    <Modal isOpen={isOpen} header={modalHeader} size="lg" onRequestClose={onRequestClose}>
      {showScopeSelector && (
        <Box mb={2}>
          <IdpScopeGrid role="radiogroup" aria-label={t('Sign-in scope')}>
            <IdpScopeCard
              name="idp-scope"
              value={IdpScope.Account}
              title={t('Tanium Account sign-in')}
              description={t('Signs users into this Tanium Account and other services that use it.')}
              checked={scope === IdpScope.Account}
              onSelect={() => setScope(IdpScope.Account)}
            />
            <IdpScopeCard
              name="idp-scope"
              value={IdpScope.Console}
              title={t('Tanium Console sign-in')}
              description={t('Configured separately inside each Tanium Cloud instance.')}
              checked={scope === IdpScope.Console}
              onSelect={() => setScope(IdpScope.Console)}
            />
          </IdpScopeGrid>
        </Box>
      )}
      <Box>
        {showConsolePicker ? (
          <ConsoleIdpInstancePicker showInstanceFriendlyNames={showInstanceFriendlyNames} onNavigate={onRequestClose} />
        ) : (
          <Formik {...formikProps}>
            {(formData: FormikProps<ConfigureIdpRequest>): React.ReactElement => (
              <IdpSetupConfiguration formData={formData} onClose={onRequestClose} />
            )}
          </Formik>
        )}
      </Box>
    </Modal>
  );
};
